import { useEffect, useState } from 'react';
import { sinRevisarVigente, type CatalogPricesProposal, type Draft, type ExpenseDraft, type ExpenseEntry, type FilaPorRevisar } from '@alexiarest/shared';
import { api } from '../api/client';
import { useDeshacerSinRevisar, useDrafts, useGuardarTodoClaro, usePorRevisar } from '../api/hooks';
import { formatMoney, today } from '../format';
import { Drawer } from './Drawer';
import { ScanReceiptDrawer, type EscaneoInicial } from './ScanReceiptDrawer';
import { fechaCorta } from './escanerFormato';
import { useToast } from './Toast';
import { VerFactura } from './VerFactura';
import './escaner.css';

/**
 * «POR REVISAR» (bloque S · reglas 6 y 10 de la sección 8b): UNA fila por
 * factura —el gasto y sus precios juntos, no dos listas— con proveedor ·
 * total · fecha · de dónde vino · qué le falta. Las «Todo claro» se guardan
 * juntas con «Guardar las N», que el servidor solo deja cuando se cumplen
 * TODAS las condiciones de la nota roja de Alex y no en los dos primeros
 * meses de la cuenta.
 */

const ORIGEN: Record<string, string> = {
  app: 'subida por ti',
  demo: 'subida por ti',
  email: 'llegó a tu correo de Alexia',
  whatsapp: 'llegó por WhatsApp',
};

function queLeFalta(f: FilaPorRevisar): { texto: string; tono: 'ambar' | 'verde' | 'rojo' } {
  switch (f.falta) {
    case 'productos': return { texto: f.porDecidir === 1 ? '1 producto por decidir' : `${f.porDecidir} productos por decidir`, tono: 'ambar' };
    case 'juntar-notas': return { texto: 'Juntar notas', tono: 'ambar' };
    case 'todo-claro': return { texto: 'Todo claro', tono: 'verde' };
    case 'repetida': return { texto: '¿Ya la tienes?', tono: 'rojo' };
    case 'proveedor-nuevo': return { texto: 'Proveedor nuevo', tono: 'ambar' };
    default: return { texto: 'Revisa los datos', tono: 'ambar' };
  }
}

function subtitulo(f: FilaPorRevisar): string {
  const partes = [fechaCorta(f.fecha), ORIGEN[f.origen] ?? 'subida por ti'];
  if (f.falta === 'juntar-notas') partes.push(`tienes ${f.notasSinFactura === 1 ? '1 nota suya' : `${f.notasSinFactura} notas suyas`} sin factura`);
  if (f.gastoGuardado) partes.push('el gasto ya está guardado');
  if (f.esNota) partes.push('nota sin factura');
  return partes.join(' · ');
}

/** Abre una fila en el escáner: el gasto y sus precios, juntos. */
export async function escaneoDeFila(f: Pick<FilaPorRevisar, 'expenseDraftId' | 'pricesDraftId' | 'proveedor' | 'total' | 'fecha' | 'supplierId'>): Promise<EscaneoInicial> {
  const [g, p] = await Promise.all([
    f.expenseDraftId ? api.getDraft(f.expenseDraftId) : Promise.resolve(undefined),
    f.pricesDraftId ? api.getDraft(f.pricesDraftId) : Promise.resolve(undefined),
  ]);
  const precios = p?.kind === 'catalog-prices' ? p : undefined;
  if (g?.kind === 'expense') {
    return {
      proposal: g.payload.proposal, key: g.inputKey, draftId: g.id, documentType: g.payload.documentType,
      catalogPrices: precios?.payload, catalogDraftId: precios?.id,
    };
  }
  // Solo quedan productos de una factura ya guardada.
  const factura = precios?.payload.factura;
  const proposal: ExpenseDraft = {
    concept: factura?.proveedor ?? f.proveedor, supplierId: factura?.supplierId ?? f.supplierId, date: factura?.fecha ?? f.fecha,
    category: 'compras_materia_prima', nature: 'variable', netAmount: factura?.total ?? f.total, vatPct: 0, invoiceTotal: factura?.total ?? f.total,
    paymentType: 'contado', invoiceStatus: 'facturado', paid: true,
    confianza: { proveedor: 'alta', fecha: 'alta', importe: 'alta', categoria: 'alta' }, avisos: [],
    ...(factura?.esNota ? { esNota: true } : {}),
  };
  return { proposal, catalogPrices: precios?.payload, catalogDraftId: precios?.id, gastoGuardado: true };
}

/** Escáner abierto sobre un borrador concreto (desde «Por revisar», Borradores o Alexia). */
export function useAbrirEscaneo() {
  const toast = useToast();
  const [abierto, setAbierto] = useState<EscaneoInicial | null>(null);
  const abrir = (f: Parameters<typeof escaneoDeFila>[0]) => {
    escaneoDeFila(f).then(setAbierto).catch((e) => toast.fail(e));
  };
  return { abierto, abrir, cerrar: () => setAbierto(null) };
}

export function PorRevisarLista({ onAbrir }: { onAbrir: (f: FilaPorRevisar) => void }): JSX.Element | null {
  const query = usePorRevisar();
  const guardar = useGuardarTodoClaro();
  const toast = useToast();
  const filas = query.data?.filas ?? [];
  const lasN = query.data?.guardarLasN;
  if (!filas.length) return null;
  const n = lasN?.ids.length ?? 0;
  return (
    <div className="prv">
      <div className="prv-lista">
        {filas.map((f) => {
          const falta = queLeFalta(f);
          return (
            <button key={f.id} type="button" className="prv-fila" onClick={() => onAbrir(f)}>
              <span className="prv-fila__main">
                <strong>{f.proveedor}{f.total ? ` · ${formatMoney(f.total)}` : ''}</strong>
                <span>{subtitulo(f)}</span>
              </span>
              <span className={`esc-chip esc-chip--${falta.tono}`}>{falta.texto}</span>
              <span className="prv-fila__accion">{f.falta === 'todo-claro' ? 'Ver' : 'Revisar'}</span>
            </button>
          );
        })}
      </div>
      {lasN?.visible && n > 0 && (
        <div className="prv-claras">
          <p>{n === 1 ? '1 factura está' : `${n} facturas están`} <strong>todo claro</strong>: proveedor conocido, productos reconocidos y no hay nada raro.</p>
          <button
            type="button"
            className="btn"
            disabled={guardar.isPending}
            onClick={() => guardar.mutate(lasN.ids, {
              onSuccess: (r) => toast.success(
                `${r.guardadas.length === 1 ? 'Guardé 1 factura' : `Guardé ${r.guardadas.length} facturas`}. Las verás en Gastos como «Guardada sin revisar» unos días, con su foto y «Deshacer».`
                + (r.saltadas.length ? ` ${r.saltadas.length === 1 ? '1 se quedó' : `${r.saltadas.length} se quedaron`} para que la mires tú.` : ''),
              ),
              onError: (e) => toast.fail(e),
            })}
          >
            {guardar.isPending ? 'Guardando…' : `Guardar ${n === 1 ? 'la factura' : `las ${n}`}`}
          </button>
        </div>
      )}
      <p className="prv-pie">Las facturas que reenvías a tu correo de Alexia llegan aquí solas.</p>
    </div>
  );
}

/**
 * Lo de Gastos: el aviso de arriba («4 por revisar · Ver los 4», con la
 * propuesta de juntar notas a la vista), el panel con la lista y el escáner
 * para la fila que se abra. Solo sale si hay algo.
 */
export function PorRevisar({ onGuardado, abrirLista, onCerrarLista }: {
  onGuardado?: (expense: ExpenseEntry) => void;
  /** Abrir el panel desde fuera (p. ej. tras subir varias fotos). */
  abrirLista?: boolean;
  onCerrarLista?: () => void;
}): JSX.Element | null {
  const query = usePorRevisar();
  const escaneo = useAbrirEscaneo();
  const [lista, setLista] = useState(false);
  const [aparte, setAparte] = useState<string[]>([]);
  const filas = query.data?.filas ?? [];
  const verLista = lista || Boolean(abrirLista);
  const cerrarLista = () => { setLista(false); onCerrarLista?.(); };
  const juntar = filas.find((f) => f.falta === 'juntar-notas' && !aparte.includes(f.id));
  const leidas = filas.filter((f) => f !== juntar).length;

  return (
    <>
      {filas.length > 0 && (
        <section className="prv-aviso" aria-label="Facturas por revisar">
          <div className="prv-aviso__cabeza">
            <strong>{filas.length} por revisar</strong>
            <button type="button" className="esc-enlace prv-aviso__ver" onClick={() => setLista(true)}>{filas.length === 1 ? 'Verla' : `Ver las ${filas.length}`}</button>
          </div>
          {juntar && (
            <div className="prv-aviso__junta">
              <p>
                Subiste una factura de <strong>{juntar.proveedor}</strong> ({formatMoney(juntar.total)}, del {fechaCorta(juntar.fecha)}) y tienes{' '}
                <strong>{juntar.notasSinFactura === 1 ? '1 nota suya' : `${juntar.notasSinFactura} notas suyas`} sin factura</strong>. ¿Esta factura junta algunas?
              </p>
              <div className="esc-prod__botones">
                <button type="button" className="btn btn--sm" onClick={() => escaneo.abrir(juntar)}>Juntar notas</button>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAparte((a) => [...a, juntar.id])}>No, es aparte</button>
              </div>
            </div>
          )}
          {leidas > 0 && (
            <p className="prv-aviso__resto">
              {juntar ? 'Y ' : ''}{leidas === 1 ? '1 factura que leí' : `${leidas} facturas que leí`} de tus fotos o tu correo: revisa que esté bien lo que saqué.
            </p>
          )}
        </section>
      )}
      <Drawer
        open={verLista && !escaneo.abierto}
        title={`Por revisar${filas.length ? ` · ${filas.length}` : ''}`}
        subtitle="Facturas que leí y aún no guardaste. Una fila por factura, con todo dentro: el gasto y sus precios."
        onClose={cerrarLista}
        width="35rem"
      >
        {filas.length ? <PorRevisarLista onAbrir={(f) => escaneo.abrir(f)} /> : <p className="esc-gris">No queda nada por revisar.</p>}
      </Drawer>
      <ScanReceiptDrawer
        open={Boolean(escaneo.abierto)}
        initialDraft={escaneo.abierto ?? undefined}
        onClose={escaneo.cerrar}
        onGuardado={onGuardado}
      />
    </>
  );
}

/**
 * «Guardada sin revisar» (nota roja de Alex): durante 7 días, en gris en la
 * lista de Gastos, con la foto a un toque y «Deshacer». Así, si algo estaba
 * mal, se arregla y se ve.
 */
export function SinRevisar({ expense }: { expense: Pick<ExpenseEntry, 'entryId' | 'guardadaSinRevisar' | 'attachmentKey'> }): JSX.Element | null {
  const deshacer = useDeshacerSinRevisar();
  const toast = useToast();
  if (!sinRevisarVigente(expense, today())) return null;
  return (
    <span className="sin-revisar cell-sub">
      <span className="esc-chip esc-chip--gris">Guardada sin revisar</span>
      {expense.attachmentKey && <VerFactura attachmentKey={expense.attachmentKey} etiqueta="Ver foto" />}
      <button
        type="button"
        className="esc-enlace"
        disabled={deshacer.isPending}
        onClick={() => deshacer.mutate(expense.entryId, {
          onSuccess: () => toast.success('Deshecha: la factura volvió a «Por revisar» y los precios a como estaban.'),
          onError: (e) => toast.fail(e),
        })}
      >
        Deshacer
      </button>
    </span>
  );
}

/**
 * El escáner único sobre un borrador de gasto o de precios (Borradores,
 * Alexia): carga su pareja del mismo archivo y lo abre todo junto.
 */
export function EscanerDeBorrador({ draft, onClose }: { draft: Draft; onClose: () => void }): JSX.Element | null {
  const toast = useToast();
  const todos = useDrafts().data;
  const [escaneo, setEscaneo] = useState<EscaneoInicial | null>(null);
  useEffect(() => {
    if (!todos) return;
    let vivo = true;
    escaneoDeFila(filaDeBorrador(draft, todos)).then((e) => { if (vivo) setEscaneo(e); }).catch((e) => { toast.fail(e); onClose(); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.id, Boolean(todos)]);
  if (!escaneo) return null;
  return <ScanReceiptDrawer open initialDraft={escaneo} onClose={onClose} />;
}

/** Para Borradores y Alexia: el escáner de un borrador de gasto o de precios. */
export function filaDeBorrador(draft: Draft, todos: Draft[] = []): Parameters<typeof escaneoDeFila>[0] {
  const pareja = (k: Draft['kind']) => todos.find((d) => d.kind === k && d.status === 'pending' && Boolean(draft.inputKey) && d.inputKey === draft.inputKey);
  if (draft.kind === 'expense') {
    return { expenseDraftId: draft.id, pricesDraftId: pareja('catalog-prices')?.id, proveedor: draft.payload.proposal.concept, total: draft.payload.proposal.invoiceTotal ?? draft.payload.proposal.netAmount, fecha: draft.payload.proposal.date };
  }
  const payload = (draft as Extract<Draft, { kind: 'catalog-prices' }>).payload as CatalogPricesProposal;
  return { expenseDraftId: pareja('expense')?.id, pricesDraftId: draft.id, proveedor: payload.factura?.proveedor ?? 'Factura', total: payload.factura?.total ?? 0, fecha: payload.factura?.fecha ?? draft.createdAt.slice(0, 10) };
}
